import { Box, Flex, Input, InputGroup, InputLeftElement, TableContainer, Text } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import ReactSelect from "react-select";
import { RiSearchLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import SubmissionsTable from "../../../containers/admin/correct-student-exams/submissions-table";
import CorrectExamModal from "../../../containers/admin/correct-student-exams/correct-exam-modal";
import PreviewCorrectionModal from "../../../containers/admin/correct-student-exams/preview-correction-modal";
import { getAllExamsRequest } from "../../../modules/admin-modules/exams/Actions";
import { getStudentExamsByExamRequest } from "../../../modules/admin-modules/student-exams/Actions";
import { matchesStudentCodeSearch } from "../../../utils/studentCodeSearch";

const examSelectBase = adminSelectStyles({ menuZIndex: 1600 });

const selectStyles = {
  ...examSelectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...examSelectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
    minWidth: "280px",
  }),
};

const CorrectStudentExams = () => {
  const dispatch = useDispatch();
  const [examId, setExamId] = useState("");
  const [studentCodeSearch, setStudentCodeSearch] = useState("");
  const [selectedStudentExam, setSelectedStudentExam] = useState(null);
  const [correctModalOpen, setCorrectModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const exams = useSelector((state) => state.exams.exams);
  const examSubmissions = useSelector((state) => state.adminStudentExams.examSubmissions);
  const isLoading = useSelector((state) => state.adminStudentExams.isLoading);

  useEffect(() => {
    dispatch(getAllExamsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!examId) return;
    dispatch(getStudentExamsByExamRequest(examId));
  }, [examId, dispatch]);

  const examOptions = useMemo(
    () =>
      (exams || []).map((exam) => ({
        value: exam.id,
        label: exam.name,
      })),
    [exams]
  );

  const selectedExamOption = useMemo(
    () => examOptions.find((opt) => String(opt.value) === String(examId)) ?? null,
    [examOptions, examId]
  );

  const filteredSubmissions = useMemo(
    () =>
      (examSubmissions || []).filter((row) =>
        matchesStudentCodeSearch(row?.student?.user_name, studentCodeSearch)
      ),
    [examSubmissions, studentCodeSearch]
  );

  const handleCorrect = (row) => {
    setSelectedStudentExam(row);
    setCorrectModalOpen(true);
  };

  const handlePreviewCorrection = (row) => {
    setSelectedStudentExam(row);
    setPreviewModalOpen(true);
  };

  const handleCloseCorrectModal = () => {
    setCorrectModalOpen(false);
    setSelectedStudentExam(null);
  };

  const handleClosePreviewModal = () => {
    setPreviewModalOpen(false);
    setSelectedStudentExam(null);
  };

  return (
    <Layout>
      <Box p={{ base: 4, md: 6 }}>
        <Flex align="center" justify="space-between" mb={6} flexWrap="wrap" gap={4}>
          <Box>
            <Text fontSize="2xl" fontWeight="800" color="var(--text-primary)">
              Correct Student Exams
            </Text>
            <Text fontSize="sm" color="var(--text-muted)" mt={1}>
              Review submitted PDFs and enter marks per question.
            </Text>
          </Box>
          <LogoutMenu />
        </Flex>

        <Flex
          mb={6}
          gap={4}
          flexWrap="wrap"
          align="flex-end"
        >
          <Box flex="1" minW={{ base: "100%", md: "280px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase" mb={2}>
              Select exam
            </Text>
            <ReactSelect
              options={examOptions}
              value={selectedExamOption}
              onChange={(opt) => {
                setExamId(opt?.value ?? "");
                setStudentCodeSearch("");
              }}
              styles={selectStyles}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              placeholder="Choose an exam…"
              isClearable
            />
          </Box>

          {examId ? (
            <Box flex="1" minW={{ base: "100%", md: "240px" }}>
              <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase" mb={2}>
                Search by student code
              </Text>
              <InputGroup>
                <InputLeftElement pointerEvents="none" color="var(--text-muted)">
                  <RiSearchLine size={18} />
                </InputLeftElement>
                <Input
                  value={studentCodeSearch}
                  onChange={(e) => setStudentCodeSearch(e.target.value)}
                  placeholder="e.g. AQ-001 or AQ-001 to AQ-049"
                  bg="var(--surface-raised)"
                  borderColor="var(--border-subtle)"
                  borderRadius="14px"
                  pl={10}
                />
              </InputGroup>
            </Box>
          ) : null}
        </Flex>

        {examId ? (
          <TableContainer
            borderWidth="1px"
            borderColor="var(--border-subtle)"
            borderRadius="xl"
            bg="var(--surface-raised)"
            p={4}
          >
            <SubmissionsTable
              rows={filteredSubmissions}
              isLoading={isLoading}
              onCorrect={handleCorrect}
              onPreviewCorrection={handlePreviewCorrection}
              searchActive={studentCodeSearch.trim().length > 0}
            />
          </TableContainer>
        ) : (
          <Text fontSize="sm" color="var(--text-muted)" py={8} textAlign="center">
            Select an exam to view student submissions.
          </Text>
        )}
      </Box>

      <CorrectExamModal
        isOpen={correctModalOpen}
        onClose={handleCloseCorrectModal}
        studentExam={selectedStudentExam}
        examId={examId}
      />

      <PreviewCorrectionModal
        isOpen={previewModalOpen}
        onClose={handleClosePreviewModal}
        studentExam={selectedStudentExam}
      />
    </Layout>
  );
};

export default CorrectStudentExams;
